import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import {
  Terminal, Plus, ArrowLeft, BookOpen, Hash, Tag, Shield,
  GitBranch, Zap, MessageSquare, Star, Network, Code2, ChevronRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/* ─── tiny helpers ─── */
function SectionAnchor({ id }: { id: string }) {
  return <span id={id} className="-mt-20 pt-20 block" aria-hidden />;
}

function Tag2({ children, color = 'orange' }: { children: React.ReactNode; color?: 'orange' | 'emerald' | 'indigo' | 'amber' }) {
  const colours: Record<string, string> = {
    orange:  'bg-orange-500/10 border-orange-500/30 text-orange-300',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    indigo:  'bg-indigo-500/10 border-indigo-500/30 text-indigo-300',
    amber:   'bg-amber-500/10 border-amber-500/30 text-amber-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-mono font-medium ${colours[color]}`}>
      {children}
    </span>
  );
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="rounded-xl overflow-hidden border border-white/[0.08] my-4">
      {title && (
        <div className="px-4 py-2 bg-white/[0.03] border-b border-white/[0.06] text-xs text-muted-foreground font-mono">
          {title}
        </div>
      )}
      <pre className="p-4 bg-white/[0.02] overflow-x-auto text-sm font-mono text-foreground/80 leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4 rounded-xl border border-white/[0.08]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08] bg-white/[0.03]">
            {headers.map((h) => (
              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-muted-foreground align-top">
                  {j === 0 ? <code className="text-orange-300/90 text-xs">{cell}</code> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NAV_ITEMS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'kinds',       label: 'Event Kinds' },
  { id: 'kind-38473',  label: '↳ Kind 38473 · Skill' },
  { id: 'kind-31309',  label: '↳ Kind 31309 · MCP' },
  { id: 'discovery',   label: 'Discovery' },
  { id: 'trust',       label: 'Trust & WoT' },
  { id: 'anchor',      label: '↳ Anchor npub' },
  { id: 'reviews',     label: 'Reviews' },
  { id: 'vouching',    label: 'Vouching' },
  { id: 'paid',        label: 'Paid Skills (DVM)' },
  { id: 'examples',    label: 'Examples' },
  { id: 'compat',      label: 'Compatibility' },
];

export default function NipPage() {
  useSeoMeta({
    title: 'SkillForge Protocol (NIP) - SkillForge',
    description: 'Full specification for the SkillForge Nostr protocol: kinds 38473 and 31309, discovery, web of trust, reviews, and paid skills.',
  });

  const { user } = useCurrentUser();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
              <Terminal className="w-4 h-4 text-orange-400" />
            </div>
            <span className="font-semibold text-lg tracking-tight">SkillForge</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
            <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
            <Link to="/nip" className="text-foreground font-medium">Protocol</Link>
            <Link to="/publish" className="hover:text-foreground transition-colors opacity-50 hover:opacity-100 text-xs">Publish</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Home
          </Link>
        </div>

        <div className="flex gap-10 lg:gap-16">
          {/* Sticky sidebar TOC */}
          <aside className="hidden lg:block w-52 shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">On this page</p>
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`block text-sm px-2 py-1 rounded-lg transition-colors hover:text-foreground hover:bg-white/[0.04] ${item.label.startsWith('↳') ? 'pl-5 text-xs text-muted-foreground/70' : 'text-muted-foreground'}`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 max-w-3xl">

            {/* Hero */}
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-5">
                <BookOpen className="w-3.5 h-3.5" />
                SkillForge Protocol Specification
              </div>
              <h1 className="text-4xl font-bold tracking-tight mb-4">
                The SkillForge NIP
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                SkillForge uses Nostr to publish and discover AI agent skills and MCP servers.
                Everything is an open, signed event — readable, auditable, and relay-portable by any Nostr client.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <Tag2 color="orange">kind:38473 · OpenClaw Skill</Tag2>
                <Tag2 color="emerald">kind:31309 · MCP Server</Tag2>
                <Tag2 color="indigo">kind:1111 · Reviews</Tag2>
                <Tag2 color="amber">kind:7 · Vouching</Tag2>
              </div>
            </div>

            <hr className="border-white/[0.06] mb-12" />

            {/* ── OVERVIEW ── */}
            <SectionAnchor id="overview" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold">Overview</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Skills are <strong className="text-foreground">code packages</strong> that agents can discover, install, and execute locally.
                MCP servers are <strong className="text-foreground">Model Context Protocol</strong> endpoints that provide tools and resources to AI assistants.
                Both are represented as Nostr <em>addressable events</em> so they are uniquely identified by their author's pubkey and a <code className="text-orange-300/90 text-xs bg-white/[0.04] px-1 py-0.5 rounded">d</code> tag slug.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Because every event is cryptographically signed, authorship is unforgeable. No company can silently swap out a skill's code, ban a publisher, or remove content from the network.
              </p>
              <div className="glass rounded-xl p-5 border border-white/[0.08]">
                <p className="text-sm font-medium mb-2">Key design principles</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  {[
                    'Addressable events — identified by pubkey + kind + d-tag',
                    'Fully open — any client can read, publish, or index skills',
                    'Trust via Web of Trust — reviews weighted by social graph proximity',
                    'Paid execution opt-in via NIP-90 DVM (publisher-operated)',
                    'No proprietary registry — lives on standard Nostr relays',
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <ChevronRight className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* ── EVENT KINDS ── */}
            <SectionAnchor id="kinds" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                  <Hash className="w-4 h-4 text-indigo-400" />
                </div>
                <h2 className="text-2xl font-bold">Event Kinds</h2>
              </div>
              <Table
                headers={['Kind', 'Name', 'Type']}
                rows={[
                  ['38473', 'OpenClaw Skill', 'Addressable (30000–39999)'],
                  ['31309', 'MCP Server', 'Addressable (30000–39999)'],
                  ['1111', 'Review / Comment', 'Regular (NIP-22)'],
                  ['7', 'Vouch / Reaction', 'Regular (NIP-25)'],
                  ['5900', 'Skill Execution Request (DVM)', 'Regular (NIP-90)'],
                  ['6900', 'Skill Execution Result (DVM)', 'Regular (NIP-90)'],
                  ['7000', 'Job Feedback (DVM)', 'Regular (NIP-90)'],
                ]}
              />
            </section>

            {/* ── KIND 38473 ── */}
            <SectionAnchor id="kind-38473" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                  <Code2 className="w-4 h-4 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold">Kind 38473 · OpenClaw Skill</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                An addressable event representing an AI agent skill that can be discovered, installed, and executed.
              </p>
              <CodeBlock title="Event structure">{`{
  "kind": 38473,
  "pubkey": "<publisher-pubkey>",
  "content": "",  // Optional: additional markdown documentation
  "tags": [
    ["d",           "<slug>"],           // Required: URL-safe identifier
    ["name",        "<name>"],           // Required: human-readable name
    ["description", "<description>"],    // Required: what this skill does
    ["version",     "<semver>"],         // Required: e.g. "1.2.0"
    ["alt",         "OpenClaw Skill: <name>"], // Required: NIP-31

    // Optional
    ["repository",  "<git-url>"],        // Source code repo
    ["homepage",    "<url>"],            // Project homepage / docs
    ["license",     "<spdx-id>"],        // e.g. "MIT", "Apache-2.0"
    ["install",     "<command>"],        // Custom install instructions
    ["t",           "<topic>"],          // Category tag (repeatable)

    // Paid skills only
    ["price",       "<msats>"]           // Price in millisatoshis
  ]
}`}</CodeBlock>
              <p className="text-sm font-semibold mt-6 mb-2">Required tags</p>
              <Table
                headers={['Tag', 'Description']}
                rows={[
                  ['d', 'Unique URL-safe slug. Combined with pubkey forms the canonical address.'],
                  ['name', 'Human-readable name shown in UIs.'],
                  ['description', 'What this skill does. Shown in search results.'],
                  ['version', 'Semantic version string (MAJOR.MINOR.PATCH).'],
                  ['alt', 'NIP-31 summary for clients that do not understand kind 38473.'],
                ]}
              />
              <p className="text-sm font-semibold mt-6 mb-2">Optional tags</p>
              <Table
                headers={['Tag', 'Description']}
                rows={[
                  ['repository', 'Git URL to source code. Enables auditing and trust verification.'],
                  ['homepage', 'Project homepage or documentation URL.'],
                  ['license', 'SPDX license identifier. Helps agents assess usage rights.'],
                  ['install', 'Custom install command; overrides default openclaw install <d> behaviour.'],
                  ['t', 'Topic/category tag. Multiple allowed. Enables relay-level filtering.'],
                  ['price', 'Price in millisats. Only set if you operate a DVM server (see §Paid Skills).'],
                ]}
              />
            </section>

            {/* ── KIND 31309 ── */}
            <SectionAnchor id="kind-31309" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <Code2 className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold">Kind 31309 · MCP Server</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                An addressable event representing a Model Context Protocol server that provides tools and resources to AI assistants.
                The tag schema is identical to kind 38473 — only the kind number and <code className="text-orange-300/90 text-xs bg-white/[0.04] px-1 py-0.5 rounded">alt</code> prefix differ.
              </p>
              <CodeBlock title="Event structure">{`{
  "kind": 31309,
  "pubkey": "<publisher-pubkey>",
  "content": "",
  "tags": [
    ["d",           "<slug>"],
    ["name",        "<name>"],
    ["description", "<description>"],
    ["version",     "<semver>"],
    ["alt",         "MCP Server: <name>"],  // ← prefix differs

    // Same optional tags as kind 38473
    ["repository",  "<git-url>"],
    ["homepage",    "<url>"],
    ["license",     "<spdx-id>"],
    ["install",     "<command>"],
    ["t",           "<topic>"],
    ["price",       "<msats>"]
  ]
}`}</CodeBlock>
            </section>

            {/* ── DISCOVERY ── */}
            <SectionAnchor id="discovery" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <Tag className="w-4 h-4 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold">Discovery</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Skills and MCPs are queried with standard Nostr filters. Tag-based filtering happens at the relay level for efficiency.
              </p>
              <CodeBlock title="Nostr filter examples">{`// All skills
{ "kinds": [38473], "limit": 50 }

// All MCP servers
{ "kinds": [31309], "limit": 50 }

// Both kinds at once
{ "kinds": [38473, 31309], "limit": 100 }

// Filter by topic tag (relay-level, efficient)
{ "kinds": [38473, 31309], "#t": ["web-scraping"] }

// Multiple topics (OR semantics)
{ "kinds": [38473, 31309], "#t": ["ai", "automation"] }

// By author
{ "kinds": [38473, 31309], "authors": ["<hex-pubkey>"] }

// Specific skill by slug + author
{ "kinds": [38473], "authors": ["<pubkey>"], "#d": ["web-scraper"] }`}</CodeBlock>
              <div className="glass rounded-xl p-4 border border-amber-500/10 mt-4">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Tip:</strong> Single-letter tags (<code className="text-orange-300/90">t</code>, <code className="text-orange-300/90">d</code>) are indexed by relays and support efficient server-side filtering. Multi-letter tags are not indexed — filter those client-side.
                </p>
              </div>
            </section>

            {/* ── TRUST ── */}
            <SectionAnchor id="trust" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                  <Network className="w-4 h-4 text-indigo-400" />
                </div>
                <h2 className="text-2xl font-bold">Trust &amp; Web of Trust</h2>
              </div>
              <div className="glass rounded-xl p-4 border border-red-500/20 mb-6">
                <p className="text-sm font-semibold text-red-400 mb-1">⚠ Critical for agents</p>
                <p className="text-sm text-muted-foreground">
                  Before executing any skill, agents <strong className="text-foreground">MUST</strong> verify trust. Skills can be malicious — they may exfiltrate data, execute harmful code, or compromise systems.
                  Raw review/vouch counts alone are <strong className="text-foreground">not</strong> trustworthy and can be gamed via sybil accounts.
                </p>
              </div>

              {/* Anchor npub callout */}
              <SectionAnchor id="anchor" />
              <div className="glass rounded-xl p-5 border border-indigo-500/20 mb-6 space-y-3">
                <p className="text-sm font-semibold text-indigo-300">The WoT anchor problem — and its solution</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  WoT trust is only meaningful when rooted in a real human's social graph.
                  A fresh agent keypair has zero follows, making its own WoT score useless.
                  An agent <em>without</em> an anchor cannot distinguish genuine reviews from coordinated sybil attacks.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Agents do not need their own Nostr keypair for trust.</strong> Instead, they should be configured with a trusted human's npub — the <strong className="text-foreground">anchor npub</strong> (typically the operator's or logged-in user's npub). The agent fetches that person's kind:3 follow list and uses it as the root of the trust graph, inheriting their full social graph instantly.
                </p>
                <CodeBlock>{`// Agent configuration (pseudo-code)
{
  "anchor_npub": "npub1...",   // operator's or user's npub — required for WoT
  "trusted_scorer": "npub1..." // optional NIP-85 scorer as fallback
}`}</CodeBlock>
                <p className="text-xs text-muted-foreground">
                  If the user is logged into SkillForge, their npub is automatically used as the anchor.
                  For headless agents, the anchor npub must be set explicitly in agent configuration.
                  Without any anchor, the agent MUST treat all skills as untrusted and rely on NIP-85 only.
                </p>
              </div>

              <p className="text-sm font-semibold mb-2">Step 0 — Resolve the anchor npub</p>
              <CodeBlock title="Fetch anchor's follow list (kind 3)">{`// The anchor npub is the operator's or logged-in user's npub (hex)
{ "kinds": [3], "authors": ["<anchor-hex-pubkey>"], "limit": 1 }

// Optionally expand to follow-of-follow (2 hops)
{ "kinds": [3], "authors": ["<follow-1>", "<follow-2>", ...] }`}</CodeBlock>

              <p className="text-sm font-semibold mb-2 mt-6">WoT weighting (relative to anchor npub)</p>
              <Table
                headers={['Reviewer relationship to anchor', 'Weight']}
                rows={[
                  ['Anchor npub itself', '1.0'],
                  ['Directly followed by anchor', '1.0'],
                  ['Follow-of-follow (2 hops)', '0.5'],
                  ['3 hops away', '0.25'],
                  ['Unknown / outside graph', '0.0 (ignored)'],
                ]}
              />

              <p className="text-sm font-semibold mb-2 mt-6">Trust decision matrix</p>
              <Table
                headers={['Anchor configured?', 'Publisher WoT', 'WoT-weighted reviews', 'Decision']}
                rows={[
                  ['Yes', '≥ 70 (high)', 'Positive from WoT', '✅ Safe to use'],
                  ['Yes', 'Unknown', 'Positive from WoT', '⚠️ Use with caution'],
                  ['Yes', 'Unknown', 'No WoT reviews', '❌ Do not use'],
                  ['Yes', '< 40 (low)', 'Any', '❌ Do not use'],
                  ['No (NIP-85 only)', '≥ 70', 'Any', '⚠️ Use with caution'],
                  ['No anchor, no NIP-85', '—', 'Raw counts only', '❌ Do not use'],
                ]}
              />

              <p className="text-sm font-semibold mb-2 mt-6">NIP-85 fallback (when no anchor is available)</p>
              <CodeBlock title="Fetch publisher's WoT score (NIP-85 Trusted Assertions)">{`{
  "kinds": [30382],
  "authors": ["<trusted-scorer-pubkey>"],
  "#d": ["<skill-publisher-pubkey>"]
}
// rank >= 70 → caution OK  |  rank < 40 → do not use`}</CodeBlock>
            </section>

            {/* ── REVIEWS ── */}
            <SectionAnchor id="reviews" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold">Reviews (NIP-22 Comments)</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Reviews use <strong className="text-foreground">kind 1111</strong> comments scoped to a skill via its addressable coordinate.
                They are plain signed Nostr events — no platform can delete or fabricate them.
              </p>
              <CodeBlock title="Review event (kind 1111)">{`{
  "kind": 1111,
  "pubkey": "<reviewer-pubkey>",
  "content": "Rock-solid. Been running in production for a month — zero issues.",
  "tags": [
    // Root scope — the skill being reviewed
    ["A", "38473:<skill-pubkey>:<identifier>", "<relay-hint>"],
    ["K", "38473"],
    ["P", "<skill-author-pubkey>"],

    // Immediate parent (same as root for top-level reviews)
    ["a", "38473:<skill-pubkey>:<identifier>", "<relay-hint>"],
    ["k", "38473"],
    ["p", "<skill-author-pubkey>"]
  ]
}`}</CodeBlock>
              <CodeBlock title="Query reviews for a skill">{`{ "kinds": [1111], "#A": ["38473:<pubkey>:<identifier>"] }`}</CodeBlock>
              <div className="glass rounded-xl p-4 border border-white/[0.08] mt-4">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Star ratings</strong> are not encoded in a separate tag — SkillForge renders ⭐ sentiment from the review text / emoji. Numerical ratings may be standardised in a future revision.
                </p>
              </div>
            </section>

            {/* ── VOUCHING ── */}
            <SectionAnchor id="vouching" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <Star className="w-4 h-4 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold">Vouching (NIP-25 Reactions)</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Quick endorsements or flags are expressed as NIP-25 reactions (<strong className="text-foreground">kind 7</strong>) on the skill event.
                These are lighter-weight than full reviews and useful for WoT-weighted aggregate scoring.
              </p>
              <CodeBlock title="Vouch (endorse)">{`{
  "kind": 7,
  "content": "+",
  "tags": [
    ["e", "<skill-event-id>"],
    ["p", "<skill-publisher-pubkey>"]
  ]
}`}</CodeBlock>
              <CodeBlock title="Flag (concern)">{`{
  "kind": 7,
  "content": "-",
  "tags": [
    ["e", "<skill-event-id>"],
    ["p", "<skill-publisher-pubkey>"]
  ]
}`}</CodeBlock>
              <p className="text-muted-foreground text-sm mt-3">
                Apply the same WoT weights as reviews. Three vouches from followed accounts outweigh 100 from strangers.
              </p>
            </section>

            {/* ── PAID SKILLS ── */}
            <SectionAnchor id="paid" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold">Paid Skills — NIP-90 DVM</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Publishers who want to charge for execution run a <strong className="text-foreground">NIP-90 Data Vending Machine</strong>.
                Free skills require no server — agents clone and run locally.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="glass-card rounded-xl p-4">
                  <p className="font-semibold text-sm mb-1">Free skill</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• No <code className="text-orange-300/90">price</code> tag</li>
                    <li>• Agent clones repo &amp; runs locally</li>
                    <li>• No server infrastructure required</li>
                    <li>• Fully decentralised</li>
                  </ul>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="font-semibold text-sm mb-1">Paid skill</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• <code className="text-orange-300/90">price</code> tag set in millisats</li>
                    <li>• Publisher runs DVM server</li>
                    <li>• Agent sends kind 5900 request</li>
                    <li>• Payment via Lightning or Cashu</li>
                  </ul>
                </div>
              </div>

              <p className="text-sm font-semibold mb-3">Payment flow</p>
              {[
                { step: '1', title: 'Agent sends job request (kind 5900)', code: `{
  "kind": 5900,
  "pubkey": "<agent-pubkey>",
  "content": "",
  "tags": [
    ["i", "38473:<provider-pubkey>:<identifier>", "event"],
    ["param", "query", "weather in Tokyo"],
    ["bid", "50000"],
    ["relays", "wss://relay.damus.io"],
    ["p", "<provider-pubkey>"]
  ]
}` },
                { step: '2', title: 'Provider requests payment (kind 7000)', code: `{
  "kind": 7000,
  "pubkey": "<provider-pubkey>",
  "content": "",
  "tags": [
    ["status", "payment-required"],
    ["amount", "50000", "<bolt11-invoice>"],
    ["e", "<job-request-id>"],
    ["p", "<agent-pubkey>"]
  ]
}` },
                { step: '3', title: 'Agent pays (Lightning or Cashu)', code: `// Agent pays the bolt11 invoice out-of-band
// or sends Cashu tokens directly.` },
                { step: '4', title: 'Provider delivers result (kind 6900)', code: `{
  "kind": 6900,
  "pubkey": "<provider-pubkey>",
  "content": "{\\"result\\": \\"Weather in Tokyo: 22°C, Sunny\\"}",
  "tags": [
    ["request", "<stringified-kind-5900-event>"],
    ["e", "<job-request-id>"],
    ["p", "<agent-pubkey>"],
    ["amount", "50000"]
  ]
}` },
              ].map(({ step, title, code }) => (
                <div key={step} className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-[10px] font-bold text-orange-400">{step}</div>
                    <p className="text-sm font-medium">{title}</p>
                  </div>
                  <CodeBlock>{code}</CodeBlock>
                </div>
              ))}
            </section>

            {/* ── EXAMPLES ── */}
            <SectionAnchor id="examples" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                  <GitBranch className="w-4 h-4 text-indigo-400" />
                </div>
                <h2 className="text-2xl font-bold">Examples</h2>
              </div>
              <CodeBlock title="Free skill — kind 38473">{`{
  "kind": 38473,
  "pubkey": "abc123...",
  "content": "",
  "tags": [
    ["d",           "web-scraper"],
    ["name",        "Web Scraper"],
    ["description", "Extract structured data from any website"],
    ["version",     "1.2.0"],
    ["alt",         "OpenClaw Skill: Web Scraper"],
    ["repository",  "https://github.com/example/web-scraper"],
    ["license",     "MIT"],
    ["t",           "scraping"],
    ["t",           "web"]
  ]
}`}</CodeBlock>
              <CodeBlock title="MCP Server — kind 31309">{`{
  "kind": 31309,
  "pubkey": "def456...",
  "content": "",
  "tags": [
    ["d",           "filesystem-mcp"],
    ["name",        "Filesystem MCP"],
    ["description", "Read and write local files for AI assistants"],
    ["version",     "2.1.0"],
    ["alt",         "MCP Server: Filesystem MCP"],
    ["repository",  "https://github.com/example/filesystem-mcp"],
    ["license",     "Apache-2.0"],
    ["t",           "filesystem"],
    ["t",           "tools"]
  ]
}`}</CodeBlock>
              <CodeBlock title="Paid skill — kind 38473 with DVM">{`{
  "kind": 38473,
  "pubkey": "ghi789...",
  "content": "",
  "tags": [
    ["d",           "premium-analyzer"],
    ["name",        "Premium Data Analyzer"],
    ["description", "Advanced data analysis powered by proprietary ML models"],
    ["version",     "3.0.0"],
    ["alt",         "OpenClaw Skill: Premium Data Analyzer"],
    ["price",       "100000"],    // 100 sats per execution
    ["t",           "analytics"],
    ["t",           "ml"]
  ]
}`}</CodeBlock>
            </section>

            {/* ── COMPATIBILITY ── */}
            <SectionAnchor id="compat" />
            <section className="mb-14">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold">Compatibility</h2>
              </div>
              <Table
                headers={['NIP', 'Used for']}
                rows={[
                  ['NIP-01', 'Core protocol — event signing, relay communication'],
                  ['NIP-02', 'Follow lists (kind 3) for WoT graph construction'],
                  ['NIP-19', 'bech32 identifiers (npub, naddr) for sharing'],
                  ['NIP-22', 'Comments (kind 1111) for skill reviews'],
                  ['NIP-25', 'Reactions (kind 7) for vouching / flagging'],
                  ['NIP-31', 'alt tag for unknown-event handling'],
                  ['NIP-85', 'Trusted Assertions (kind 30382) for WoT scoring'],
                  ['NIP-89', 'Handler recommendations for DVM discoverability'],
                  ['NIP-90', 'Data Vending Machines (kinds 5900 / 6900 / 7000) for paid execution'],
                ]}
              />
            </section>

          </main>
        </div>
      </div>
    </div>
  );
}
